import { FC, useMemo, useState } from 'react'
import { useParams, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MenuButton } from 'components/buttons'
import { Spinner } from 'components/loaders'
import { useAdminApprovalGroups } from '@features/admin/hooks/useAdminApprovalGroups'
import { useEditApprovalGroup } from '../../hooks/useEditApprovalGroup'
import { faEdit } from '@fortawesome/pro-solid-svg-icons'
import { ApproversSection } from './components/ApproversSection'
import { DefaultGroupManagerSection } from './components/DefaultGroupManagerSection'
import { EditDefaultGroupManagerModal } from './components/EditDefaultGroupManagerModal'
import { toast } from '@lib/toasts'
import { NewHeader as Header } from 'components/page-header'
import {
  EditApproverModal,
  AddApproverModal,
  useApproverManagement,
  AutoApprovalSection,
} from '@features/admin/features/approval-domain-approvers'
import { useToggleAutoApproval } from '../../hooks/useApprovalGroupsManagement'

export const ApprovalGroupDetails: FC = () => {
  const { t } = useTranslation()
  const { approvalGroupId } = useParams<{ approvalGroupId: string }>()
  const {
    data: approvalGroups,
    isLoading: approvalGroupsLoading,
    refetch,
  } = useAdminApprovalGroups()

  const approvalGroup = useMemo(
    () => approvalGroups?.find((g) => g.id === Number(approvalGroupId)),
    [approvalGroups, approvalGroupId],
  )
  const toggleAutoApprovalMutation = useToggleAutoApproval()

  const [
    showEditDefaultGroupManagerModal,
    setShowEditDefaultGroupManagerModal,
  ] = useState(false)

  const { edit } = useEditApprovalGroup()

  const {
    editingApprover,
    setEditingApprover,
    showAddApproverModal,
    setShowAddApproverModal,
    handleDeleteApprover,
    handleApproverSuccess,
    handleApproverError,
  } = useApproverManagement(approvalGroup, () => void refetch())

  if (approvalGroupsLoading) {
    return <Spinner />
  }

  if (!approvalGroup) {
    return <Navigate to="/approval-groups" replace />
  }

  const actionMenuItems = [
    {
      icon: faEdit,
      label: t('features.approvalGroupsAdmin.approvalGroupDetails.renameGroup'),
      onClick: () => edit(approvalGroup),
    },
  ]

  return (
    <>
      <div className="flex flex-col flex-grow">
        <Header
          title={approvalGroup.name}
          renderRightSlot={() => (
            <MenuButton title={t('common.action')} menuItems={actionMenuItems}>
              {t('common.action')}
            </MenuButton>
          )}
        />
        <div className="flex flex-col p-8">
          <ApproversSection
            approvalGroup={approvalGroup}
            onAdd={() => setShowAddApproverModal(true)}
            onEdit={(approver) => setEditingApprover(approver.id)}
            onDelete={handleDeleteApprover}
          />
          <DefaultGroupManagerSection
            approvalGroup={approvalGroup}
            onEdit={() => setShowEditDefaultGroupManagerModal(true)}
          />
          <AutoApprovalSection
            approvalDomainType="approval group"
            approvalDomain={approvalGroup}
            mutateFn={toggleAutoApprovalMutation.mutate}
          />
        </div>
      </div>
      {showEditDefaultGroupManagerModal && (
        <EditDefaultGroupManagerModal
          approvalGroup={approvalGroup}
          onClose={() => setShowEditDefaultGroupManagerModal(false)}
          onError={() =>
            toast({
              title: t(
                'features.approvalGroupsAdmin.unableToSaveApprovalGroup',
              ),
              variant: 'error',
              content: t(
                'features.approvalGroupsAdmin.failedToSaveApprovalGroup',
              ),
            })
          }
          onSuccess={() =>
            toast({
              title: t('features.approvalGroupsAdmin.approvalGroupSaved'),
              variant: 'success',
              content: t(
                'features.approvalGroupsAdmin.approvalGroupDetails.successfullySavedDefaultGroupManager',
              ),
            })
          }
        />
      )}
      {editingApprover && (
        <EditApproverModal
          approvalDomainId={approvalGroup.globalId}
          approverId={editingApprover}
          onClose={() => setEditingApprover(undefined)}
          onError={handleApproverError}
          onSuccess={handleApproverSuccess}
        />
      )}
      {showAddApproverModal && (
        <AddApproverModal
          approvalDomainId={approvalGroup.globalId}
          onClose={() => setShowAddApproverModal(false)}
          onError={handleApproverError}
          onSuccess={handleApproverSuccess}
          userIdsAlreadyAsApprover={
            new Set(approvalGroup.approverUsers.map((u) => u.id))
          }
        />
      )}
    </>
  )
}
